import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Check, Loader2, Calendar, Share2, Sparkles } from "lucide-react";

export const Route = createFileRoute("/exclusive-partner-deal")({
  head: () => ({
    meta: [
      { title: "Inside Salesforce — Live Q&A | SaaSed" },
      { property: "og:title", content: "Inside Salesforce — Live Q&A | SaaSed" },
      { property: "og:description", content: "A live 20-minute Q&A for CIOs, CFOs and Procurement Leaders on Salesforce roadmap, renewals and investment decisions." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "A live 20-minute Q&A for CIOs, CFOs and Procurement Leaders on Salesforce roadmap, renewals and investment decisions.",
      },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/exclusive-partner-deal" }],
  }),
  component: ExclusivePartnerDealPage,
});

const topics = [
  "Agentforce",
  "Data Cloud",
  "Salesforce Roadmap",
  "AI Initiatives",
  "Renewal Strategy",
  "Commercial Trends",
  "Platform Investment Decisions",
];

function ExclusivePartnerDealPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("utm_source") !== "mailchimp") {
      // Preserve incoming query string (UTMs etc.) when redirecting home.
      navigate({ to: "/", search: (prev: Record<string, unknown>) => ({ ...prev }) });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">
      <NavBar />
      <main>
        <Hero />
        <IntroStrip />
        <ValueSection />
        <TopicsSection />
        <StrategicInsights />
        <FinalCtaSection />
        <Footer />
      </main>
    </div>
  );
}

/* -------------------------------- NAV -------------------------------- */
function NavBar() {
  return (
    <header className="border-b border-brand-ink/10 bg-brand-cream">
      <div className="container-x flex items-center justify-between py-5">
        <span className="font-serif text-xl font-semibold tracking-tight">SaaSed</span>
        <a
          href="#register"
          className="font-sans text-sm font-medium uppercase tracking-[0.15em] border border-brand-ink px-5 py-2.5 hover:bg-brand-ink hover:text-brand-cream transition-colors"
        >
          Reserve Your Spot
        </a>
      </div>
    </header>
  );
}

/* -------------------------------- HERO ------------------------------- */
function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-brand-ink/10">
      <div className="container-x py-20 md:py-28 relative">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70 mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            Live Q&A · By Invitation
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Inside Salesforce:{" "}
            <span className="text-brand-ink/60">
              Roadmap, Renewals & Investment Decisions
            </span>
          </h1>
          <div className="mt-10 space-y-3">
            <p className="font-serif text-xl md:text-2xl">
              A live 20-minute Q&A{" "}
              <span className="text-brand-ink/70">
                for CIOs, CFOs and Procurement Leaders.
              </span>
            </p>
            <p className="inline-flex items-center gap-2 font-sans text-sm font-medium uppercase tracking-[0.15em] text-brand-ink">
              <Calendar className="h-4 w-4" />
              Wednesday, 1st of July · 13:00–13:20 EET
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- INTRO STRIP --------------------------- */
function IntroStrip() {
  return (
    <section className="bg-brand-primary border-b border-brand-ink/10">
      <div className="container-x py-16 md:py-20">
        <div className="grid md:grid-cols-[1fr,auto] gap-10 items-center">
          <div className="max-w-3xl">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70 mb-4">
              Your exclusive invitation
            </p>
            <p className="font-serif text-2xl md:text-3xl leading-snug">
              Anders Gustafsson, former Salesforce leader and SaaSed co-founder,
              is hosting a live 20-minute Q&A focused on the questions
              organisations are already discussing internally around Salesforce
              roadmap, renewals and investment decisions.
            </p>
          </div>
          <a
            href="#register"
            className="font-sans text-sm font-medium uppercase tracking-[0.15em] bg-brand-ink text-brand-cream px-6 py-3.5 hover:bg-brand-ink/90 transition-colors whitespace-nowrap"
          >
            Reserve Your Spot
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- VALUE SECTION -------------------------- */
function ValueSection() {
  return (
    <section className="border-b border-brand-ink/10">
      <div className="container-x py-20 md:py-28 grid md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70 mb-4">
            01 · Context
          </p>
          <h2 className="font-serif text-3xl md:text-4xl mb-8 leading-tight">
            Why This Session is Valuable for You
          </h2>
          <div className="space-y-5 font-sans text-base leading-relaxed text-brand-ink/85">
            <p>
              As Agentforce London approaches, Anders will share fresh
              observations and perspectives from the event, helping
              organisations understand what recent developments may mean for
              their roadmap, investments and future renewals.
            </p>
            <p>
              Salesforce continues to evolve rapidly. New products, AI
              initiatives, platform investments and commercial changes are
              creating new opportunities, new uncertainties and new decisions
              for leadership teams.
            </p>
            <p>
              This session is an opportunity to bring your questions, hear
              different perspectives and discuss what these developments could
              mean for your organisation.
            </p>
            <p>
              Whether you're evaluating new Salesforce investments, planning
              your roadmap, thinking about future renewals or simply trying to
              understand Salesforce's direction, this session is for you.
            </p>
          </div>
        </div>
        <div>
          <figure className="border border-brand-ink/15 bg-brand-ivory p-8 md:p-10 h-full flex flex-col justify-center">
            <span className="font-serif text-6xl leading-none text-brand-ink/40">"</span>
            <blockquote className="font-serif text-xl md:text-2xl leading-snug mt-4">
              SaaSed helped bring structure and clarity to a renewal that had
              grown increasingly complex. The process was disciplined,
              data-driven, and delivered a stronger long-term commercial
              position than we expected.
            </blockquote>
            <figcaption className="mt-8 font-sans text-sm font-medium uppercase tracking-[0.15em] text-brand-ink/70">
              — Matthew Toynton, CFO, PHMG
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- TOPICS SECTION -------------------------- */
function TopicsSection() {
  return (
    <section className="bg-brand-ivory border-b border-brand-ink/10">
      <div className="container-x py-20 md:py-24">
        <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70 mb-3">
          Agenda Preview
        </p>
        <h2 className="font-serif text-3xl md:text-4xl mb-10 leading-tight">
          Topics We Will Cover Include
        </h2>
        <ul className="flex flex-wrap gap-3">
          {topics.map((t) => (
            <li
              key={t}
              className="inline-flex items-center gap-2 border border-brand-ink/30 bg-brand-cream px-4 py-2.5 font-sans text-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand-punch" />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* -------------------- STRATEGIC INSIGHTS -------------------- */
function StrategicInsights() {
  return (
    <section className="bg-brand-ivory border-b border-brand-ink/10">
      <div className="container-x py-20 md:py-28">
        <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70 mb-4">
          02 · Countermeasures
        </p>
        <h2 className="font-serif text-3xl md:text-4xl mb-12 leading-tight">
          Strategic Countermeasures – Leveling the Playing Field
        </h2>
        
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl mb-4 leading-tight">
              1. The "single point of contact" rule
            </h1>
            <div className="font-sans text-base leading-relaxed text-brand-ink/85 space-y-4">
              <p>
                Salesforce often employs a "divide and conquer" strategy, reaching out to different stakeholders (IT, Finance, Marketing) simultaneously.
              </p>
              <p>
                <strong>The risk:</strong> Different internal teams may inadvertently leak information or express urgency that weakens your negotiation leverage.
              </p>
              <p>
                <strong>The fix:</strong> Channel all communications through a single point of contact. This ensures a unified front and prevents Salesforce from exploiting internal silos.
              </p>
            </div>
          </div>

          <div>
            <h1 className="font-serif text-2xl md:text-3xl mb-4 leading-tight">
              2. Beware the bundling trap
            </h1>
            <div className="font-sans text-base leading-relaxed text-brand-ink/85 space-y-4">
              <p>
                A common tactic is offering deep unit price discounts in exchange for bundling new, "exciting" products.
              </p>
              <p>
                <strong>The reality:</strong> If you aren't ready to implement these extra products immediately, they become a sunk cost. A "great deal" on paper is a loss if the ROI doesn't materialize.
              </p>
              <p>
                <strong>Strategic move:</strong> Align product additions strictly with your operational capacity, not with the AE's quarterly incentives.
              </p>
            </div>
          </div>

          <div>
            <h1 className="font-serif text-2xl md:text-3xl mb-4 leading-tight">
              3. The power of the "compelling event"
            </h1>
            <div className="font-sans text-base leading-relaxed text-brand-ink/85 space-y-4">
              <p>
                Salesforce uses their quarterly and fiscal year-ends to drive urgency. You can flip this.
              </p>
              <p>
                <strong>Leverage:</strong> Use their internal deadlines to structure your deal. An AE who has committed a deal to their manager is often more flexible in the final hours to ensure the "ink is on the paper."
              </p>
            </div>
          </div>

          <div>
            <h1 className="font-serif text-2xl md:text-3xl mb-4 leading-tight">
              4. Navigating the professional gap
            </h1>
            <div className="font-sans text-base leading-relaxed text-brand-ink/85 space-y-4">
              <p>
                There is a natural asymmetry in these negotiations. Salesforce deals with these contracts thousands of times a day; most companies do it once every few years.
              </p>
              <p>
                <strong>The insider edge:</strong> This is where the complexity "tax" is most expensive. Navigating 1,000+ SKUs and shifting product names requires more than just a roadmap — it requires deep market intelligence.
              </p>
              <p>
                <strong>Expertise as an asset:</strong> Recognizing when the complexity exceeds internal resources is a strategic decision. Engaging a specialized procurement partner ensures you aren't just reacting to Salesforce's playbook, but rewriting the terms to favor your organization's long-term ROI.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------- FINAL CTA / FORM ------------------------- */
function FinalCtaSection() {
  return (
    <section id="register" className="bg-brand-ink text-brand-cream">
      <div className="container-x py-20 md:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-primary mb-4">
            02 · Reserve
          </p>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">
            Reserve your spot today.
          </h2>
          <p className="font-sans text-base md:text-lg text-brand-cream/80 leading-relaxed max-w-md">
            Join the live Q&A and walk into your next Salesforce conversation
            with clarity and confidence.
          </p>
          <div className="mt-10 pt-10 border-t border-brand-cream/15 space-y-2 font-sans text-sm text-brand-cream/70">
            <p>
              <span className="text-brand-cream">When</span> · Wednesday, 1st of
              July · 13:00–13:20 EET
            </p>
            <p>
              <span className="text-brand-cream">Format</span> · Live 20-min Q&A
              (online)
            </p>
            <p>
              <span className="text-brand-cream">Host</span> · Anders
              Gustafsson, SaaSed co-founder
            </p>
          </div>
        </div>
        <RegistrationForm />
      </div>
    </section>
  );
}

/* --------------------------- REGISTRATION ---------------------------- */
const MAILCHIMP_ACTION =
  "https://saased.us22.list-manage.com/subscribe/post?u=b0b56a1a3a62f7fdadb7fe265&id=51a44aab1c&f_id=00afc2e1f0";
const MAILCHIMP_HONEYPOT = "b_b0b56a1a3a62f7fdadb7fe265_51a44aab1c";

function RegistrationForm() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    if (submitting || submitted) return;
    setSubmitting(true);
    // Mailchimp responds in the hidden iframe; assume success after submit.
    setTimeout(() => {
      setSubmitted(true);
      setSubmitting(false);
    }, 800);
  };

  if (submitted) {
    return (
      <div className="bg-brand-cream text-brand-ink p-8 md:p-10 border border-brand-primary">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary">
          <Check className="h-6 w-6 text-brand-ink" />
        </div>
        <h3 className="font-serif text-2xl md:text-3xl mt-6">
          You're on the list!
        </h3>
        <p className="font-sans text-base text-brand-ink/75 mt-3 leading-relaxed">
          Thanks for registering. We've sent a confirmation to your email and
          look forward to seeing you on Wednesday, 1st of July.
        </p>
        <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/60 mt-8 mb-3">
          Share with a colleague
        </p>
        <div className="flex gap-3">
          <button type="button" aria-label="Post about this SaaSed partner event on social media" className="inline-flex items-center gap-2 border border-brand-ink px-4 py-2 font-sans text-sm hover:bg-brand-ink hover:text-brand-cream transition-colors">
            <Share2 className="h-4 w-4" aria-hidden="true" /> Post
          </button>
          <button type="button" aria-label="Share this SaaSed partner event with a colleague" className="inline-flex items-center gap-2 border border-brand-ink px-4 py-2 font-sans text-sm hover:bg-brand-ink hover:text-brand-cream transition-colors">
            <Share2 className="h-4 w-4" aria-hidden="true" /> Share
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <iframe
        name="mc-submit-target"
        title="mailchimp-submit"
        style={{ display: "none" }}
      />
      <form
        action={MAILCHIMP_ACTION}
        method="post"
        target="mc-submit-target"
        onSubmit={handleSubmit}
        className="bg-brand-cream text-brand-ink p-8 md:p-10 border border-brand-primary"
      >
        <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
          RSVP Here
        </p>
        <p className="font-sans text-xs text-brand-ink/55 mt-1">
          * indicates required
        </p>
        <div className="mt-6 space-y-4">
          <input type="hidden" name="tags" value="952" />
          {/* Mailchimp bot-trap honeypot — must remain empty */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-5000px" }}>
            <input type="text" name={MAILCHIMP_HONEYPOT} tabIndex={-1} defaultValue="" />
          </div>

          <div>
            <label
              htmlFor="EMAIL"
              className="block font-sans text-xs font-medium uppercase tracking-[0.15em] text-brand-ink/70 mb-2"
            >
              Email <span className="text-brand-ink/40">*</span>
            </label>
            <input
              id="EMAIL"
              name="EMAIL"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-brand-ink/30 bg-brand-ivory px-4 py-3 font-sans text-sm text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-ink focus:ring-2 focus:ring-brand-ink/20 transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full font-sans text-sm font-medium uppercase tracking-[0.15em] bg-brand-ink text-brand-cream px-6 py-4 hover:bg-brand-ink/90 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Sending…" : "Reserve Your Spot"}
          </button>
          <p className="font-sans text-xs text-brand-ink/55 text-center">
            Instant confirmation. No spam — ever. Unsubscribe in one click.
          </p>
        </div>
      </form>
    </>
  );
}

/* -------------------------------- FOOTER ----------------------------- */
function Footer() {
  return (
    <footer className="bg-brand-cream border-t border-brand-ink/10">
      <div className="container-x py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="font-sans text-sm text-brand-ink/60">
          © 2026 SaaSed.co Limited
        </p>
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-brand-ink/50">
          Private invitation · Do not share publicly
        </p>
      </div>
    </footer>
  );
}
